import { useEffect, useRef, useState } from "react";
import { Compass, RotateCcw, PlayCircle, ChevronDown } from "lucide-react";
import { useTour } from "./TourContext";
import { TourHub } from "./TourHub";

/**
 * Header-mounted "?" button that opens a small menu listing every tour
 * registered by the currently-mounted page (and any sibling pages that
 * also mounted on this route — usually just the active one). Clicking
 * an item replays that tour; "Reset all tours" clears every dismissal
 * so first-visit auto-start triggers again.
 *
 * The menu also has a "Getting started" entry that opens {@link TourHub} —
 * the full-catalog onboarding modal — for tours the current page doesn't
 * register (other pages' tours + the cross-page flows).
 */
export function TourLauncher() {
  const { registry, start, resetAllDismissals } = useTour();
  const [open, setOpen] = useState(false);
  const [hubOpen, setHubOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const tours = Object.values(registry);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Primary tour for this page — first one registered. Clicking the
  // pill body starts it directly (force-replays even if dismissed);
  // clicking the caret opens the menu for siblings + admin actions.
  // When no tour is registered, the pill still opens the menu so the
  // "Getting started" hub and "Reset all tours" remain reachable.
  const primary = tours[0];

  const startPrimary = () => {
    if (primary) start(primary, { force: true });
    else setOpen(true);
  };

  return (
    <div ref={ref} className="relative">
      <div className="inline-flex items-stretch overflow-hidden rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 shadow-sm transition hover:bg-emerald-100">
        <button
          type="button"
          onClick={startPrimary}
          aria-label={primary ? `Replay tour: ${primary.title}` : "Open guided tours"}
          title={primary ? `Replay: ${primary.title}` : "Guided tours"}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium"
        >
          <Compass className="h-3.5 w-3.5" aria-hidden="true" />
          <span>Tour</span>
        </button>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label="Open guided-tour menu"
          aria-expanded={open}
          title="More tours and reset"
          className="inline-flex items-center border-l border-emerald-200 px-1.5 py-1 hover:bg-emerald-200/60"
        >
          <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-72 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg ring-1 ring-slate-900/5">
          <div className="border-b border-slate-100 bg-slate-50 px-3 py-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Guided tours
            </div>
            <div className="mt-0.5 text-[11px] text-slate-500">
              {tours.length === 0 ? "No tour registered for this page." : "Replay any tour any time."}
            </div>
          </div>

          {/* Onboarding hub — the full catalog, including other pages'
              tours and cross-page flows the current page doesn't register. */}
          <button
            type="button"
            onClick={() => {
              setHubOpen(true);
              setOpen(false);
            }}
            className="flex w-full items-start gap-2 border-b border-slate-100 px-3 py-2.5 text-left transition hover:bg-emerald-50"
          >
            <Compass className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-900">Getting started</div>
              <div className="text-xs text-slate-500">Browse all tours &amp; track progress</div>
            </div>
          </button>

          {tours.length > 0 && (
            <ul className="max-h-72 overflow-auto py-1">
              {tours.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => {
                      start(t, { force: true });
                      setOpen(false);
                    }}
                    className="flex w-full items-start gap-2 px-3 py-2 text-left transition hover:bg-emerald-50"
                  >
                    <PlayCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-900">{t.title}</div>
                      {t.subtitle && (
                        <div className="truncate text-xs text-slate-500">{t.subtitle}</div>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t border-slate-100 px-3 py-2">
            <button
              type="button"
              onClick={() => {
                resetAllDismissals();
                setOpen(false);
              }}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Reset all tours
            </button>
          </div>
        </div>
      )}
      <TourHub open={hubOpen} onClose={() => setHubOpen(false)} />
    </div>
  );
}
